import { PaginatedResponse, useApi } from '@/api';
import { Asset, EtherscanLink, Page } from '@/components';

import { useSearchParams } from '@/hooks';
import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  DropdownFilter,
  Flex,
  formatDatetime,
  Heading,
  NetworkIcon,
  ProtocolIcon,
  RelativeDateTime,
  TableSkeleton,
  useIsMobile,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { DropdownFilterOption } from '@blockanalitica/ui/package/types/src/components/molecules/TableFilters/DropdownFilter';
import _ from 'lodash';
import { useCallback } from 'react';

type Event = {
  datetime: string;
  order_index: string;
  block_number: number;
  tx_hash: string;
  wallet_address: string;
  event: string;
  amount: string;
  balance: string;
  sender: string;
  receiver: string;
  address: string;
  asset_address: string;
  network: string;
  symbol: string;
  asset_symbol: string;
  name: string;
  networks: [{ value: string; label: string }];
};

type Dropdown = DropdownFilterOption & {
  address?: string;
};

type EventsResponse = PaginatedResponse<Event[]> & {
  networks: Dropdown[];
  protocols: Dropdown[];
  assets: Dropdown[];
};

export default function SLLActivityPage() {
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedNetwork: '',
      selectedAsset: '',
      selectedProtocol: ''
    }
  });
  const [date] = useSearchParams('date', '');
  const [data, error] = useApi<EventsResponse>({
    url: '/sparkstar/sll/events/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      date: date,
      network: filters.filtersApplied['selectedNetwork'],
      asset: filters.filtersApplied['selectedAsset'],
      protocol: filters.filtersApplied['selectedProtocol']
    }
  });
  const isMobile = useIsMobile();
  const fromDate = date ? ` on ${formatDatetime(date)}` : '';

  const networkFilterIcons = useCallback((option: Dropdown) => {
    return <NetworkIcon network={option.value as string} size="2" />;
  }, []);
  const protocolFilterIcons = useCallback((option: Dropdown) => {
    return <ProtocolIcon protocol={option.label} size="2" />;
  }, []);
  const assetsFilterIcons = useCallback((option: Dropdown) => {
    return (
      <CryptoIcon
        name={option.label}
        address={option.address}
        network={option.network}
        size="2"
      />
    );
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Network',
      getCell: (row) => (
        <Flex gap="xs" alignItems="center">
          <NetworkIcon network={row.network} size="2" />
          {_.capitalize(row.network)}
        </Flex>
      ),
      columnSize: isMobile ? '100px' : '150px'
    },
    {
      header: 'Protocol',
      getCell: (row) =>
        row.protocol === null ? (
          '-'
        ) : (
          <Flex gap="xs" alignItems="center">
            <ProtocolIcon protocol={row.protocol} size="2" />
            {_.capitalize(row.protocol)}
          </Flex>
        )
    },
    {
      header: 'Underying Asset',
      getCell: (row) => (
        <>
          {row.asset_address ? (
            <Asset
              symbol={row.asset_symbol}
              address={row.asset_address}
              network={row.network}
              iconSize="2"
              showName={false}
            />
          ) : (
            '-'
          )}
        </>
      )
    },

    {
      header: 'Asset',
      getCell: (row) => row.symbol,
      sticky: true
    },

    {
      header: 'Sender',
      getCell: (row) =>
        row.sender === '0x0000000000000000000000000000000000000000' ? (
          '-'
        ) : (
          <WalletAddress
            address={row.sender}
            options={{ name: row.sender_label }}
          />
        )
    },
    {
      header: 'Receiver',
      getCell: (row) =>
        row.receiver === '0x0000000000000000000000000000000000000000' ? (
          '-'
        ) : (
          <WalletAddress
            address={row.receiver}
            options={{ name: row.receiver_label }}
          />
        )
    },

    {
      header: 'Amount',
      getCell: (row) => <CurrencyValue value={row.amount} />,
      // getCellBottom: (row) => (
      //   <TableCellBottom checkValue={row.amount_usd}>
      //     <CurrencyValue
      //       value={row.amount}
      //       options={{
      //         absolute: true
      //       }}
      //     />
      //   </TableCellBottom>
      // ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <EtherscanLink value={row.tx_hash} network={row.network} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];
  return (
    <Page
      title={<Heading>Spark Liquidity Layer Activity {fromDate}</Heading>}
      actions={
        <Flex flexDirection="column">
          <Flex gap={['s', 'xs']}>
            <DropdownFilter
              multi={true}
              filterField="selectedNetwork"
              options={data.networks || []}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Networks (${filteredCount})`
              }
              optionPrefixFn={networkFilterIcons}
              {...filters}
            />
            <DropdownFilter
              multi={true}
              filterField="selectedProtocol"
              options={data.protocols || []}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Protocols (${filteredCount})`
              }
              optionPrefixFn={protocolFilterIcons}
              {...filters}
            />
            <DropdownFilter
              multi={true}
              filterField="selectedAsset"
              options={data.assets || []}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Underlying Assets (${filteredCount})`
              }
              optionPrefixFn={assetsFilterIcons}
              {...filters}
            />
          </Flex>
        </Flex>
      }>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => `${row.order_index}`}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    </Page>
  );
}
